import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Tabs,
  Tab,
  Box,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ReactRouterLink from "./ReactRouterLink";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import store from "../../redux/store";
import {
  fetchCartProducts,
  fetchCartReset,
  removeCart,
  updateCartQuantity,
  createCart,
  PAGE_SIZE,
} from "../../redux/actions/cartAction";
import {
  fetchWishlistProducts,
  removeWishlist,
  PAGE_SIZE as WISHLIST_PAGE_SIZE,
} from "../../redux/actions/wishlistAction";
import { Product } from "../../redux/types/productTypes";
import { getLoginCookieAndSession } from "../../utils";
import SnackbarAlert, { SnackbarRef } from "./SnackbarAlert";
import { FREE_SHIPPING_PRICE } from "../../redux/types/productTypes";

interface IProps {
  onDeleteCartItem: () => void;
  isOpen?: boolean;
}

const Cart: React.FC<IProps> = ({ onDeleteCartItem, isOpen }: IProps) => {
  const dispatch = useDispatch();
  const snackbarRef = useRef<SnackbarRef>(null);
  const profileId = getLoginCookieAndSession();

  const { loading, cartData } = useSelector((state: RootState) => state.carts);
  const { products } = cartData;
  const loadingRef = useRef(loading);

  // Keep loading ref in sync
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const { loading: wishlistLoading, wishlistData } = useSelector(
    (state: RootState) => state.wishlists
  );
  const { products: wishlistProducts } = wishlistData;
  const wishlistLoadingRef = useRef(wishlistLoading);

  // Keep wishlist loading ref in sync
  useEffect(() => {
    wishlistLoadingRef.current = wishlistLoading;
  }, [wishlistLoading]);

  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [updatingItems, setUpdatingItems] = useState<Set<number>>(new Set());
  const [deletingCartItems, setDeletingCartItems] = useState<Set<number>>(
    new Set()
  );
  const [updatingWishlistItems, setUpdatingWishlistItems] = useState<
    Set<number>
  >(new Set());
  const [addingToCartFromWishlist, setAddingToCartFromWishlist] = useState<
    Set<number>
  >(new Set());
  const [deletingFromWishlist, setDeletingFromWishlist] = useState<Set<number>>(
    new Set()
  );
  const [hasWishlistLoaded, setHasWishlistLoaded] = useState(false);
  const [localWishlistItems, setLocalWishlistItems] = useState<Product[]>([]);

  // Fetch cart on mount, when modal opens, or when switching to cart tab
  useEffect(() => {
    if (profileId && (isOpen || selectedTab === 0)) {
      dispatch(fetchCartProducts(Number(profileId), "", PAGE_SIZE));
    } else if (!profileId) {
      // Clear cart when user is not logged in
      dispatch(fetchCartReset());
    }
  }, [dispatch, profileId, isOpen, selectedTab]);

  // Fetch wishlist when tab is selected or modal opens
  useEffect(() => {
    if (profileId && (isOpen || selectedTab === 1)) {
      setHasWishlistLoaded(false);
      dispatch(
        fetchWishlistProducts(Number(profileId), "", WISHLIST_PAGE_SIZE)
      );
    }
  }, [dispatch, profileId, isOpen, selectedTab]);

  // Track when wishlist has loaded and sync local state
  useEffect(() => {
    if (!wishlistLoading && selectedTab === 1) {
      setHasWishlistLoaded(true);
      // Only update local items if Redux has data
      // Don't overwrite local state with empty Redux state (could be stale during refetch)
      if (wishlistProducts.length > 0) {
        setLocalWishlistItems(wishlistProducts);
      }
    }
  }, [wishlistLoading, selectedTab, wishlistProducts]);

  // Initialize local state when first switching to wishlist tab
  useEffect(() => {
    if (
      selectedTab === 1 &&
      localWishlistItems.length === 0 &&
      wishlistProducts.length === 0 &&
      !wishlistLoading &&
      hasWishlistLoaded
    ) {
      // This is a genuinely empty wishlist
      setLocalWishlistItems([]);
    }
  }, [
    selectedTab,
    localWishlistItems.length,
    wishlistProducts.length,
    wishlistLoading,
    hasWishlistLoaded,
  ]);

  // Calculate total amount
  useEffect(() => {
    if (products.length > 0) {
      const productTotal = products.reduce((acc, product) => {
        const quantity = product.cartQuantity || 1;
        const price = parseFloat(
          product.productPrice?.replace(/[^0-9.-]+/g, "") || "0"
        );
        return acc + price * quantity;
      }, 0);

      const shippingTotal = products.reduce((acc, product) => {
        const quantity = product.cartQuantity || 1;
        const shipping =
          product.productUsaShipping === FREE_SHIPPING_PRICE
            ? 0
            : parseFloat(
              product.productUsaShipping?.replace(/[^0-9.-]+/g, "") || "0"
            );
        return acc + shipping * quantity;
      }, 0);

      setTotalAmount(productTotal + shippingTotal);
    } else {
      setTotalAmount(0);
    }
  }, [products]);

  const handleRemoveItem = async (productId: number) => {
    if (!profileId) return;

    setDeletingCartItems((prev) => new Set(prev).add(productId));
    try {
      await dispatch(removeCart(Number(profileId), productId) as any);

      // Wait for cart refetch to complete and item to be removed from list
      // The removeCart action dispatches fetchCartProducts which will update the products
      // Check if product is still in the list and wait until it's removed

      // Wait for loading to complete first
      let retries = 0;
      while (loadingRef.current && retries < 50) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        retries++;
      }

      // Wait for product to be removed from list
      retries = 0;
      while (retries < 50) {
        const state = store.getState() as RootState;
        const currentProducts = state.carts.cartData.products;
        if (!currentProducts.some((p) => p.createdTime === productId)) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 50));
        retries++;
      }

      // Show success message after item is removed from list
      onDeleteCartItem();

      // Remove from deleting set after success message is shown
      setTimeout(() => {
        setDeletingCartItems((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }, 1000);
    } catch (error) {
      snackbarRef.current?.showSnackbar("Failed to remove item", "error");
      // Remove from deleting set on error
      setDeletingCartItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleIncreaseQty = async (product: Product) => {
    if (!profileId) return;

    const productId = product.createdTime;
    const currentQuantity = product.cartQuantity || 1;
    const newQuantity = currentQuantity + 1;

    setUpdatingItems((prev) => new Set(prev).add(productId));
    try {
      await dispatch(
        updateCartQuantity(Number(profileId), productId, newQuantity) as any
      );
    } catch (error: any) {
      snackbarRef.current?.showSnackbar(
        error?.message || "Failed to update quantity",
        "error"
      );
    } finally {
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleDecreaseQty = async (product: Product) => {
    if (!profileId) return;

    const productId = product.createdTime;
    const currentQuantity = product.cartQuantity || 1;
    const newQuantity = Math.max(1, currentQuantity - 1);

    setUpdatingItems((prev) => new Set(prev).add(productId));
    try {
      await dispatch(
        updateCartQuantity(Number(profileId), productId, newQuantity) as any
      );
    } catch (error: any) {
      snackbarRef.current?.showSnackbar(
        error?.message || "Failed to update quantity",
        "error"
      );
    } finally {
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleCheckout = () => {
    console.log("Proceeding to checkout");
  };

  const totalItemsCount = products.reduce(
    (acc, product) => acc + (product.cartQuantity || 1),
    0
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    // Fetch data when switching tabs
    if (profileId) {
      if (newValue === 0) {
        // Fetch cart when switching to cart tab
        dispatch(fetchCartProducts(Number(profileId), "", PAGE_SIZE));
      } else if (newValue === 1) {
        // Fetch wishlist when switching to wishlist tab
        dispatch(
          fetchWishlistProducts(Number(profileId), "", WISHLIST_PAGE_SIZE)
        );
      }
    }
  };

  const handleAddToCartFromWishlist = async (product: Product) => {
    if (!profileId) return;

    const productId = product.createdTime;

    // Check if item is out of stock
    if (product.productQuantity === 0) {
      snackbarRef.current?.showSnackbar("Sorry, item out of stock", "warning");
      return;
    }

    setAddingToCartFromWishlist((prev) => new Set(prev).add(productId));
    setUpdatingWishlistItems((prev) => new Set(prev).add(productId));

    try {
      // Add to cart
      await dispatch(createCart(Number(profileId), productId, 1) as any);

      // Remove from wishlist
      await dispatch(removeWishlist(Number(profileId), productId) as any);

      // Remove from local state immediately so item disappears
      setLocalWishlistItems((prev) =>
        prev.filter((p) => p.createdTime !== productId)
      );

      // Refetch cart to update badge
      dispatch(fetchCartProducts(Number(profileId), "", PAGE_SIZE) as any);

      // Refetch wishlist to sync with server (don't await)
      dispatch(
        fetchWishlistProducts(Number(profileId), "", WISHLIST_PAGE_SIZE) as any
      );

      // Remove from updating sets after everything completes
      setAddingToCartFromWishlist((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      setUpdatingWishlistItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });

      snackbarRef.current?.showSnackbar("Added to your cart", "success");
    } catch (error: any) {
      snackbarRef.current?.showSnackbar(
        error?.message || "Failed to add item to cart",
        "error"
      );
      // Refetch wishlist on error to sync state
      await dispatch(
        fetchWishlistProducts(Number(profileId), "", WISHLIST_PAGE_SIZE) as any
      );
      // Remove from updating sets on error
      setAddingToCartFromWishlist((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      setUpdatingWishlistItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleRemoveFromWishlist = async (productId: number) => {
    if (!profileId) return;

    setDeletingFromWishlist((prev) => new Set(prev).add(productId));
    setUpdatingWishlistItems((prev) => new Set(prev).add(productId));

    try {
      await dispatch(removeWishlist(Number(profileId), productId) as any);

      // Remove from local state immediately so item disappears
      setLocalWishlistItems((prev) =>
        prev.filter((p) => p.createdTime !== productId)
      );

      // Show success message
      snackbarRef.current?.showSnackbar("Removed from wishlist", "success");

      // Remove from updating sets
      setDeletingFromWishlist((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      setUpdatingWishlistItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });

      // Refetch wishlist to sync with server (don't await)
      dispatch(
        fetchWishlistProducts(Number(profileId), "", WISHLIST_PAGE_SIZE) as any
      );
    } catch (error: any) {
      snackbarRef.current?.showSnackbar(
        error?.message || "Failed to remove from wishlist",
        "error"
      );
      // Remove from updating sets on error
      setDeletingFromWishlist((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      setUpdatingWishlistItems((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const parsePrice = (priceString: string): number => {
    return parseFloat(priceString?.replace(/[^0-9.-]+/g, "") || "0");
  };

  const parseShipping = (shippingString: string): number => {
    if (shippingString === FREE_SHIPPING_PRICE) return 0;
    return parseFloat(shippingString?.replace(/[^0-9.-]+/g, "") || "0");
  };

  const isMaxQuantity = (product: Product): boolean => {
    const cartQuantity = product.cartQuantity || 1;
    return cartQuantity >= product.productQuantity;
  };

  // Use local state for wishlist products to prevent flickering during refetch
  const displayedWishlistProducts = localWishlistItems;

  return (
    <div style={{ maxWidth: "500px" }}>
      <Tabs value={selectedTab} onChange={handleTabChange} centered>
        <Tab label="Shopping Cart" />
        <Tab label="Wishlist" />
      </Tabs>

      <Box hidden={selectedTab !== 0}>
        {loading && products.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : products.length === 0 ? (
          <Typography
            variant="h5"
            align="center"
            marginTop="20px"
            style={{ opacity: 0.7 }}
          >
            <RemoveShoppingCartIcon
              fontSize="medium"
              style={{ marginRight: 6, opacity: 0.8 }}
            />
            Cart is empty
          </Typography>
        ) : (
          <List
            style={{
              maxHeight: "340px",
              overflowY: "auto",
              paddingRight: "6px",
            }}
          >
            {products.map((product) => {
              const quantity = product.cartQuantity || 1;
              const price = parsePrice(product.productPrice || "0");
              const shipping = parseShipping(product.productUsaShipping || "0");
              const isUpdating = updatingItems.has(product.createdTime);
              const isDeleting = deletingCartItems.has(product.createdTime);
              const maxQuantity = isMaxQuantity(product);

              return (
                <ListItem
                  key={product.createdTime}
                  style={{
                    marginBottom: "12px",
                    background: "rgba(0,0,0,0.05)",
                    borderRadius: "12px",
                    padding: "10px 12px",
                    transition: "background 0.2s ease",
                    opacity: isUpdating ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(0,0,0,0.08)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "rgba(0,0,0,0.05)")
                  }
                >
                  <ReactRouterLink
                    to={product.productUrl}
                    linkElement={
                      <img
                        src={product.productImageOne || product.productImage}
                        alt={product.productName}
                        style={{
                          width: 55,
                          height: 55,
                          objectFit: "cover",
                          borderRadius: "12px",
                          marginRight: "14px",
                        }}
                      />
                    }
                  />

                  <ListItemText
                    primary={
                      <>
                        <ReactRouterLink
                          to={product.productUrl}
                          linkElement={
                            <Typography
                              variant="body1"
                              style={{
                                color: "rgb(231, 135, 88)",
                                fontWeight: 600,
                                textDecoration: "none",
                              }}
                            >
                              {product.productName}
                            </Typography>
                          }
                        />

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginTop: 6,
                          }}
                        >
                          <span>Qty:</span>

                          <Tooltip title="Decrease Quantity">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDecreaseQty(product)}
                                disabled={isUpdating || quantity <= 1}
                                style={{
                                  border: "1px solid rgba(0,0,0,0.15)",
                                  padding: 2,
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <span
                            style={{
                              minWidth: 22,
                              textAlign: "center",
                              fontWeight: 700,
                            }}
                          >
                            {quantity}
                          </span>

                          <Tooltip
                            title={
                              maxQuantity
                                ? "Maximum quantity reached"
                                : "Increase Quantity"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleIncreaseQty(product)}
                                disabled={isUpdating || maxQuantity}
                                style={{
                                  border: "1px solid rgba(0,0,0,0.15)",
                                  padding: 2,
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Typography>

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ marginTop: 2 }}
                        >
                          Price:{" "}
                          {quantity === 1 ? (
                            <b>{product.productPrice}</b>
                          ) : (
                            <>
                              {product.productPrice} × {quantity} ={" "}
                              <b>${(price * quantity).toFixed(2)}</b>
                            </>
                          )}
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                          Shipping:{" "}
                          {quantity === 1 ? (
                            <b>
                              {product.productUsaShipping ===
                                FREE_SHIPPING_PRICE
                                ? "Free"
                                : product.productUsaShipping}
                            </b>
                          ) : (
                            <>
                              {product.productUsaShipping ===
                                FREE_SHIPPING_PRICE
                                ? "Free"
                                : product.productUsaShipping}{" "}
                              × {quantity} ={" "}
                              <b>
                                {shipping === 0
                                  ? "Free"
                                  : `$${(shipping * quantity).toFixed(2)}`}
                              </b>
                            </>
                          )}
                        </Typography>
                      </>
                    }
                  />

                  <ListItemSecondaryAction>
                    <Tooltip title="Remove Item">
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveItem(product.createdTime)}
                        disabled={isUpdating || isDeleting}
                        style={{
                          background: "rgba(0,0,0,0.06)",
                          borderRadius: "10px",
                        }}
                        onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(0,0,0,0.12)")
                        }
                        onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(0,0,0,0.06)")
                        }
                      >
                        {isDeleting ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}

        {products.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              marginTop: "16px",
              padding: "10px 14px",
              background: "rgba(0,0,0,0.05)",
              borderRadius: "10px",
              fontWeight: 700,
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.08)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.05)")
            }
          >
            <Typography
              variant="body1"
              style={{
                fontWeight: 700,
                opacity: 0.85,
              }}
            >
              Order Summary
            </Typography>

            <Typography
              variant="body1"
              align="right"
              style={{
                fontWeight: 700,
              }}
            >
              Total: ${totalAmount.toFixed(2)}
            </Typography>
          </div>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleCheckout}
          style={{
            borderRadius: "20px",
            color: "white",
            marginTop: "20px",
            width: "100%",
          }}
          disabled={products.length === 0}
        >
          {products.length === 0
            ? "Add item(s) to cart"
            : `Checkout (${totalItemsCount} item${totalItemsCount !== 1 ? "s" : ""
            })`}
        </Button>
      </Box>

      {/* Wishlist */}
      <Box hidden={selectedTab !== 1}>
        {!hasWishlistLoaded && wishlistLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : hasWishlistLoaded &&
          displayedWishlistProducts.length === 0 &&
          wishlistProducts.length === 0 &&
          !wishlistLoading &&
          addingToCartFromWishlist.size === 0 &&
          deletingFromWishlist.size === 0 ? (
          <Typography
            variant="h5"
            align="center"
            marginTop="20px"
            style={{ opacity: 0.7 }}
          >
            Your wishlist is empty
          </Typography>
        ) : displayedWishlistProducts.length === 0 &&
          addingToCartFromWishlist.size === 0 &&
          deletingFromWishlist.size === 0 &&
          !wishlistLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <List
            style={{
              maxHeight: "340px",
              overflowY: "auto",
              paddingRight: "6px",
            }}
          >
            {displayedWishlistProducts.map((product) => {
              const isUpdating = updatingWishlistItems.has(product.createdTime);
              const isAddingToCart = addingToCartFromWishlist.has(
                product.createdTime
              );
              const isDeletingFromWishlist = deletingFromWishlist.has(
                product.createdTime
              );
              const isOutOfStock = product.productQuantity === 0;

              return (
                <ListItem
                  key={product.createdTime}
                  style={{
                    marginBottom: "12px",
                    background: "rgba(0,0,0,0.05)",
                    borderRadius: "12px",
                    padding: "10px 12px",
                    paddingRight: "60px",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(0,0,0,0.08)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "rgba(0,0,0,0.05)")
                  }
                >
                  <ReactRouterLink
                    to={product.productUrl}
                    linkElement={
                      <img
                        src={product.productImageOne || product.productImage}
                        alt={product.productName}
                        style={{
                          width: 55,
                          height: 55,
                          objectFit: "cover",
                          borderRadius: "12px",
                          marginRight: "14px",
                        }}
                      />
                    }
                  />

                  <ListItemText
                    primary={
                      <>
                        <ReactRouterLink
                          to={product.productUrl}
                          linkElement={
                            <Typography
                              variant="body1"
                              style={{
                                color: "rgb(231, 135, 88)",
                                fontWeight: 600,
                                textDecoration: "none",
                              }}
                            >
                              {product.productName}
                            </Typography>
                          }
                        />

                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ marginTop: 2 }}
                        >
                          Price: <b>{product.productPrice}</b>
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                          Shipping:{" "}
                          <b>
                            {product.productUsaShipping === FREE_SHIPPING_PRICE
                              ? "Free"
                              : product.productUsaShipping}
                          </b>
                        </Typography>

                        {isOutOfStock && (
                          <Typography
                            variant="body2"
                            color="error"
                            style={{ marginTop: 4 }}
                          >
                            Out of stock
                          </Typography>
                        )}
                      </>
                    }
                  />

                  <ListItemSecondaryAction
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      top: 10,
                      transform: "none",
                    }}
                  >
                    <Tooltip title="Add to Cart">
                      <span>
                        <IconButton
                          edge="end"
                          aria-label="add to cart"
                          onClick={() => handleAddToCartFromWishlist(product)}
                          disabled={isUpdating || isOutOfStock}
                          style={{
                            background: "rgba(0,0,0,0.06)",
                            borderRadius: "10px",
                          }}
                          onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            "rgba(0,0,0,0.12)")
                          }
                          onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            "rgba(0,0,0,0.06)")
                          }
                        >
                          {isAddingToCart ? (
                            <CircularProgress size={18} />
                          ) : (
                            <AddShoppingCartIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Remove from Wishlist">
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() =>
                          handleRemoveFromWishlist(product.createdTime)
                        }
                        disabled={isUpdating}
                        style={{
                          background: "rgba(0,0,0,0.06)",
                          borderRadius: "10px",
                        }}
                        onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(0,0,0,0.12)")
                        }
                        onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(0,0,0,0.06)")
                        }
                      >
                        {isDeletingFromWishlist ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      <img
        src="/stripe.png"
        alt="Powered by Stripe"
        style={{
          height: "22px",
          verticalAlign: "text-bottom",
          float: "right",
          marginTop: "10px",
        }}
      />
      <Typography
        variant="body2"
        align="left"
        style={{ marginTop: "10px", marginBottom: "10px" }}
      >
        🛡️ Read our{" "}
        <ReactRouterLink
          to="/blog/our-protection-guarantee"
          linkElement="Buyer Protection Guarantee"
        />
      </Typography>
      <Typography
        variant="body2"
        align="left"
        style={{ marginTop: "10px", marginBottom: "10px", fontSize: "0.8rem" }}
      >
        <center>
          By continuing to checkout, you agree to the FiddlePiddle{" "}
          <ReactRouterLink
            to="/blog/privacy"
            linkElement="Privacy Policy"
            style={{ color: "grey" }}
          />{" "}
          and{" "}
          <ReactRouterLink
            to="/blog/terms"
            linkElement={"Terms of Service"}
            style={{ color: "grey" }}
          />
          .
        </center>
      </Typography>
      <SnackbarAlert ref={snackbarRef} />
    </div>
  );
};

export default Cart;
